import React, { useContext, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { AiOutlineClose } from 'react-icons/ai';
import { BiDislike, BiLike, BiSolidDislike, BiSolidLike, BiSolidMagicWand } from 'react-icons/bi';
import { BsEmojiNeutral } from "react-icons/bs";
import { FiFrown, FiSmile } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PreferenceContext } from '../../App';
import API_ROUTES from '../../core/constants/api';
import { LS_AI_CONFIG_KEY, LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY, LS_UI_ELEMENTS_CONFIG_KEY, RefinedTextRating, ReportPageType, currentYear } from '../../core/constants/common';
import AICopilotConfigService from '../../core/service/AICopilotConfigService';
import GeneralDisclosureService from '../../core/service/GeneralDisclosureService';
import ReportAIService from '../../core/service/ReportAIService';
import ReportService from '../../core/service/ReportService';
import StandardService from '../../core/service/StandardService';
import ReportingUtils from '../../core/utilities/ReportingUtils';
import Utils from '../../core/utilities/Utils';
import test from "../../images/bg1.png";
import esg_ai from '../../images/esg_ai.png';
import AIConfigButton from './AIConfigButton';
import './AITextGenWidget.scss';
import ContentBlockService from '../../core/service/ContentBlockService';

const AITextGenWidget = ({
	t,
	type = 'REPORTING_STUDIO',
	page,
	section,
	periodYear,
	regulationId,
	regulationName,
	regulationFrequency,
	materialTopic,
	onHide,
	onTransferToEditor,
	jobId,
	brsrData,
	descriptionData
}) => {

	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY));
	const aiConfig = JSON.parse(localStorage.getItem(LS_AI_CONFIG_KEY) ?? null);
	const uiElementsConfig = JSON.parse(localStorage.getItem(LS_UI_ELEMENTS_CONFIG_KEY)) ?? null;

	const [loading, setLoading] = useState(false);
	const [text, setText] = useState('');
	const [refinedText, setRefinedText] = useState({ rating: -1 });
	const [wordCount, setWordCount] = useState('150-200');
	const [styles, setStyles] = useState([]);
	const [activeStyles, setActiveStyles] = useState(aiConfig?.style ? [aiConfig.style.type] : []);
	const [tones, setTones] = useState([]);
	const [activeTones, setActiveTones] = useState(aiConfig?.tone ? [aiConfig.tone.type] : []);
	const [lastAPICallContext, setLastAPICallContext] = useState('');
	const [feedbackVal, setFeedbackVal] = useState('');
	const [description, setDescription] = useState('');
	const location = useLocation();

	const companyName = lsData.profile.company.legalName;
	const companyId = lsData.profile.company.id;
	const textArea = useRef();
	const { preference } = useContext(PreferenceContext);

	useEffect(() => {
		if (type === 'MATERIAL_TOPIC' || type === 'BRSR_STUDIO' || type === 'CONTENT_BLOCK') {
			return;
		} else if (page?.type === ReportPageType.ESG_HIGHLIGHTS) {
			generateText();
		} else {
			fetchAllStyles();
			fetchAllTones();
			const aiConfigSectionId = Utils.parseAIConfigSectionIdFromReportPageType(page.type);
			const matchIndex = aiConfig?.sections?.length > 0 ? aiConfig?.sections?.findIndex(s => s.sectionType === aiConfigSectionId) : -1;
			if (matchIndex >= 0) {
				let wCount = '150-200';
				if (aiConfig?.sections[matchIndex]?.wordCount === 'MEDIUM') {
					wCount = '200-350';
				} else if (aiConfig?.sections[matchIndex]?.wordCount === 'LONG') {
					wCount = '350-500';
				}
				setWordCount(wCount);
			}
		}
	}, []);

	useEffect(() => {
		if (!materialTopic && !brsrData && !descriptionData) return;

		generateText();
	}, [materialTopic, brsrData, descriptionData])

	const generateText = () => {
		if (type === 'MATERIAL_TOPIC') {
			getRefinedTextForMaterialTopic();
			return;
		}
		if (type === 'BRSR_STUDIO') {
			getRefinedTextForBRSR();
			return;
		}
		if (type === 'CONTENT_BLOCK') {
			getRefinedTextForContentBlock();
			return;
		}
		if (!page?.type) return;

		if (page.type === ReportPageType.ESG_HIGHLIGHTS) {
			fetchESGHighlightsDataWithMetrics();
			return;
		}
	};

	const generateConfigDependentText = () => {
		if (isESGSection(page.type)) {
			fetchStreamedDataWithDataPoints(page);
			return;
		}

		let payload = {
			data: {
				company_name: companyName,
				reporting_standard: regulationName ?? '',
				year: periodYear ?? currentYear,
				section_name: section.name ?? '',
				periodicity: regulationFrequency ?? '',
				industry: companyInfo?.industry?.name ?? '',
				country: companyInfo?.address?.country?.name ?? '',
				company_id: companyId,
				word_limit: wordCount,
				section_id: Utils.parseAIConfigSectionIdFromReportPageType(page.type),
				style_id: activeStyles,
				tone_id: activeTones,
				language: preference.language === 'en' ? 'en-us' : preference.language 
			}
		};
		if (page.type === ReportPageType.ABOUT_COMPANY) {
			payload = {
				...payload,
				data: {
					...payload.data,
					company_info: companyInfo?.about ?? '',
				}
			};
		}
		if (page.type === ReportPageType.ABOUT_REPORT) {
			payload = {
				...payload,
				data: {
					...payload.data,
					esg_frameworks: companyInfo?.esgFrameworks ?? '',
				}
			};
		}
		if (page.type === ReportPageType.ESG_GOALS) {
			payload = {
				...payload,
				data: {
					...payload.data,
					esg_goals: companyInfo?.esgGoals ?? '',
					section_name: 'ESG Goals'
				}
			};
		}
		if (page.type === ReportPageType.OTHERS) {
			payload = {
				...payload,
				data: {
					...payload.data,
					section_name: 'dynamic section',
					section_description: description,
					section_title: page.name
				}
			};
		}
		fetchStreamedData(payload, page.type);
	};

	const fetchAllStyles = () => {
			AICopilotConfigService.getStyles().then(res => {
				if (res.status !== 200) return;
		
				setStyles(res.data ?? []);
			}).catch(err => { });
	};
	
	const fetchAllTones = () => {
		AICopilotConfigService.getTones().then(res => {
			if (res.status !== 200) return;
	
			setTones(res.data ?? []);
		}).catch(err => { });
	};

	const fetchStreamedData = async (payload, type) => {
		try {
			const url = type === ReportPageType.ABOUT_COMPANY || type === ReportPageType.ABOUT_REPORT
				|| type === ReportPageType.ESG_GOALS || type === ReportPageType.OTHERS
				? ReportAIService.ReportingOverviewStreamedAPIURL
				: isESGSection(type)
					? ReportAIService.ReportContentGenStreamedAPIURL
					: ReportAIService.ESGHighglightsStreamedAPIURL;
			setLastAPICallContext({
				api_name: url.substring(url.indexOf('/esg'), url.length),
				api_input: payload
			});
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(payload)

			});

			// Handle the streamed data using a response stream
			const reader = response.body.getReader();
			let result = '';
			if (response.status === 200) {
				while (true) {
					const { done, value } = await reader.read();
					setLoading(false);
					if (done) break;
					result += new TextDecoder('utf-8').decode(value);
					setText(result);
					// Auto scroll
					const area = textArea.current;
					if (area) {
						area.scrollTop = area.scrollHeight;
					}
				}
			} else {
				setLoading(false);
				return errorHandler(response);
			}
		} catch (error) {
			console.error('Error fetching data:', error);
			setLoading(false);
		}
	};

	const copyToClipboard = () => {
		navigator.clipboard.writeText(text);
		toast.info('Copied');
	};

	const fetchStreamedDataWithDataPoints = (page) => {
		const type = page.type;
		if (isESGOverview(type)) {
			fetchESGOverviewAggregatedDataWithDataPoints();
			return;
		}
		setLoading(true);
		const topicData = JSON.parse(JSON.stringify(page.json));
		const y1 = StandardService.getQuantitativeMetricDataPointsData(
			regulationId,
			`${periodYear}-01-01`,
			ReportingUtils.getCodesByPageType(type),
			topicData ? topicData.id : 0
		);
		const y2 = StandardService.getQuantitativeMetricDataPointsData(
			regulationId,
			`${periodYear - 1}-01-01`,
			ReportingUtils.getCodesByPageType(type),
			topicData ? topicData.id : 0
		);
		const qualReq = StandardService.getQualitativeMetricQuestionAnswers(
			regulationId,
			`${periodYear}-01-01`,
			ReportingUtils.getQualitativeMetricCodesByPageType(type),
			topicData ? topicData.id : 0
		);
		const gdReq = GeneralDisclosureService.getGDQuestionAnswersByCode(
			`${periodYear}-01-01`,
			ReportingUtils.getGDQuestionCodesByPageType(type),
		);
		const cbReq = ContentBlockService.getContentBlocksForTopic(
			topicData ? topicData.id : 0,
			`${periodYear}-01-01`,
			regulationId
		);
		Promise.all([y1, y2, qualReq, gdReq, cbReq]).then(res => {
			setLoading(false);
			const y1Res = res[0].data;
			const y2Res = res[1].data;
			const qualRes = res[2].data;
			const gdRes = res[3].data;
			const cbRes = res[4].data;
			const quantitativeMetrics = [];
			const qualitativeMetrics = [];
			const caseStudies = [];
			populateWithQuantitativeDatapointData(y1Res, quantitativeMetrics, periodYear);
			populateWithQuantitativeDatapointData(y2Res, quantitativeMetrics, periodYear - 1);			
			populateWithQualitativeMetricData(qualRes, qualitativeMetrics);
			populateWithGDQuestionAnswers(gdRes.questions, qualitativeMetrics);
			populateCaseStudies(cbRes, caseStudies);
			const payload = {
				job_id: jobId,
				company_name: companyName,
				section_name: section.name,
				sub_section_name: page.name,
				industry: companyInfo?.industry?.name ?? '',
				country: companyInfo?.address?.country?.name ?? '',
				word_limit: wordCount,
				supplementary_data: [{
					topic: page.name,
					qualitative_metrics: refactorQualMetrics(qualitativeMetrics),
					quantitative_metrics: refactorQuantMetrics(quantitativeMetrics),
					case_studies: caseStudies
				}],
				company_id: companyId,
				section_id: Utils.parseAIConfigSectionIdFromReportPageType(type),
				style_id: activeStyles,
				tone_id: activeTones,
				language: preference.language === 'en' ? 'en-us' : preference.language ,
				reporting_standard: 'GRI',
    		stream: false
			};
			setLoading(true);
			fetchStreamedData(payload, type);
		}).catch(err => {
			setLoading(false);
		});
	};

	const populateWithQuantitativeDatapointData = (data, quantitativeMetrics, year) => {
		data?.forEach(metricData => {
			if (!metricData.metric) return;

			const metricName = metricData.metric.name?.trim();
			const matchingIndex = quantitativeMetrics?.findIndex(q => q.metric === metricName);
			if (matchingIndex >= 0) {
				if (metricData.value !== null) {
					quantitativeMetrics[matchingIndex].values.push({
						year: year, value: `${metricData.value}`
					});
				}
				metricData.dataPoints?.forEach(dataPoint => {
					if (!dataPoint) return;

					const dpName = dataPoint.name.trim();
					const unit = dataPoint.unit ? `${dataPoint.unit}` : '';
					const matchingDPIndex = quantitativeMetrics[matchingIndex].datapoints?.findIndex(dp => dp.datapoint === dpName);
					if (matchingDPIndex >= 0) {
						if (dataPoint.combined !== null) {
							quantitativeMetrics[matchingIndex].datapoints[matchingDPIndex].values.push({
								year: year, value: `${dataPoint.combined} ${unit}`
							});
						}
					} else {
						const dp = {
							datapoint: dpName,
							values: []
						};
						if (dataPoint.combined !== null) {
							dp.values.push({ year: year, value: `${dataPoint.combined} ${unit}` });
						}
						quantitativeMetrics[matchingIndex].datapoints.push(dp);
					}
				});
			} else {
				const qMetric = {
					metric: metricName,
					values: [],
					datapoints: [],
				};
				if (metricData.value !== null) {
					qMetric.values.push({
						year: year,
						value: `${metricData.value}`
					});
				}
				metricData.dataPoints?.forEach(dataPoint => {
					if (!dataPoint) return;

					const dpName = dataPoint.name.trim();
					const unit = dataPoint.unit ? `${dataPoint.unit}` : '';
					const dp = {
						datapoint: dpName,
						values: []
					};
					if (dataPoint.combined !== null) {
						dp.values.push({ year: year, value: `${dataPoint.combined} ${unit}` });
					}
					qMetric.datapoints.push(dp);
				});
				quantitativeMetrics.push(qMetric);
			}
		});
	};

	const populateWithQualitativeMetricData = (qualRes, qualitativeMetrics) => {
		qualRes?.forEach(metric => {
			const qualMetricData = {
				metric: metric.name?.trim(),
				questions: []
			};
			if (metric.question) {
				let answer = ' ';
				metric.question.answers?.forEach((a, aIndex) => {
					answer += `${a.answer}`;
					if (aIndex === metric.question?.answers?.length - 1) return;

					answer += `, `;
				});
				if (Utils.isNotEmpty(answer)) {
					qualMetricData.questions.push({
						question: metric.question.question?.trim(),
						answers: { answer: answer }
					});
				}
				metric.question.children?.forEach(child => {
					let childAnswer = ' ';
					child.answers?.forEach((a, aIndex) => {
						childAnswer += `${a.answer}`;
						if (aIndex === child?.answers?.length - 1) return;

						childAnswer += `, `;
					});
					if (Utils.isNotEmpty(childAnswer)) {
						qualMetricData.questions.push({
							question: child.question?.trim(),
							answers: { answer: childAnswer }
						});
					}
				});
			}
			qualitativeMetrics.push(qualMetricData);
		});
	};

	const populateWithGDQuestionAnswers = (questions, qualitativeMetrics) => {
		questions?.forEach(question => {
			const qualMetricData = {
				metric: question.name?.trim(),
				questions: []
			};
			let answer = ' ';
			question.answers?.forEach((a, aIndex) => {
				answer += `${a.answer}`;
				if (aIndex === question?.answers?.length - 1) return;

				answer += `, `;
			});
			if (Utils.isNotEmpty(answer)) {
				qualMetricData.questions.push({
					question: question.name?.trim(),
					answers: { answer: answer }
				});
			}
			question.children?.forEach(child => {
				let childAnswer = ' ';
				child.answers?.forEach((a, aIndex) => {
					childAnswer += `${a.answer}`;
					if (aIndex === child?.answers?.length - 1) return;

					childAnswer += `, `;
				});
				if (Utils.isNotEmpty(childAnswer)) {
					qualMetricData.questions.push({
						question: child.name?.trim(),
						answers: { answer: childAnswer }
					});
				}
			});
			qualitativeMetrics.push(qualMetricData);
		});
	};

	const fetchESGOverviewAggregatedDataWithDataPoints = () => {
		setLoading(true);
		ReportService.getESGOverviewPageData(regulationId, `${periodYear}-01-01`, +section.type)
			.then(res => {
				setLoading(false);
				if (res.status !== 200) return;

				let supDataList = [];
				formatQuanAndQualDataFromResponse(res.data, supDataList);
				const payload = {
					job_id: jobId,
					company_name: companyName,
					section_name: section.name,
					sub_section_name: ReportingUtils.getSubsectionNameForType(page.type),
					industry: companyInfo?.industry?.name ?? '',
					country: companyInfo?.address?.country?.name ?? '',
					word_limit: wordCount,
					supplementary_data: supDataList,
					company_id: companyId,
					section_id: Utils.parseAIConfigSectionIdFromReportPageType(page.type),
					style_id: activeStyles,
					tone_id: activeTones,
					language: preference.language === 'en' ? 'en-us' : preference.language 
				};
				setLoading(true);
				fetchStreamedData(payload, page.type);
			}).catch(err => {
				setLoading(false);
			});
	};

	const formatQuanAndQualDataFromResponse = (res, supDataList) => {
		let quantitativeMetrics = [];
		let qualitativeMetrics = [];
		Object.keys(res)?.forEach(key => {
			populateWithQuantitativeDatapointData(res[key].quantitativeData[0]?.data ?? [], quantitativeMetrics, res[key].quantitativeData[0]?.year);
			populateWithQuantitativeDatapointData(res[key].quantitativeData[1]?.data ?? [], quantitativeMetrics, res[key].quantitativeData[1]?.year);
			populateWithQualitativeMetricData(res[key].qualitativeData?.data?.questions ?? [], qualitativeMetrics);
			populateWithGDQuestionAnswers(res[key].gdData?.data?.questions ?? [], qualitativeMetrics);
			supDataList.push(
				{
					topic: key,
					qualitative_metrics: refactorQualMetrics(qualitativeMetrics),
					quantitative_metrics: refactorQuantMetrics(quantitativeMetrics)
				}
			);
		})
		
	};


	const isESGSection = (type) => {
		return type === ReportPageType.ENERGY
			|| type === ReportPageType.CARBON_EMISSION
			|| type === ReportPageType.WASTE
			|| type === ReportPageType.WATER
			|| type === ReportPageType.ENV_OVERVIEW
			|| type === ReportPageType.CUSTOMERS
			|| type === ReportPageType.COMMUNITY
			|| type === ReportPageType.EMPLOYEES
			|| type === ReportPageType.SUPPLIERS
			|| type === ReportPageType.SOCIAL_OVERVIEW
			|| type === ReportPageType.ESG_GOV
			|| type === ReportPageType.BOARD_GOV
			|| type === ReportPageType.HUMAN_RIGHTS
			|| type === ReportPageType.ETHICS_COMP
			|| type === ReportPageType.TAX_POLICY
			|| type === ReportPageType.BC_DIS_RECOVERY
			|| type === ReportPageType.GOV_OVERVIEW
			|| type === ReportPageType.ENVIRONMENT 
			|| type === ReportPageType.SOCIAL
			|| type === ReportPageType.GOVERNANCE;
	};

	const isESGOverview = (type) => {
		return type === ReportPageType.ENV_OVERVIEW
			|| type === ReportPageType.SOCIAL_OVERVIEW
			|| type === ReportPageType.GOV_OVERVIEW;
	};

	const getRefinedTextForMaterialTopic = () => {
		const esrsMappingIndex = materialTopic.mapping?.findIndex(m => m.standard === 'ESRS');
		let payload;
		if (esrsMappingIndex >= 0) {
			payload = {
				question: materialTopic.question?.trim() ?? '',
				frameworks: ['ESRS'],
				question_codes: { ESRS: materialTopic.mapping[esrsMappingIndex].frameworkCode},
				answer: materialTopic.answer?.trim() ?? '',
				language: preference.language === 'en' ? 'en-us' : preference.language
			};
		} else {
			payload = {
				company_name: companyName,
				industry: companyInfo?.industry?.name ?? '',
				materialtopic: materialTopic.materialtopic?.trim() ?? '',
				question: materialTopic.question?.trim() ?? '',
				guideline: materialTopic.guideline?.trim() ?? '',
				answer: materialTopic.answer?.trim() ?? '',
				language: preference.language === 'en' ? 'en-us' : preference.language
			};
		}
		setLoading(true);
		setLastAPICallContext({
			api_name: esrsMappingIndex >= 0 ? API_ROUTES.REPORT_AI.MATERIAL_TOPIC_ESRS : API_ROUTES.REPORT_AI.MATERIAL_TOPIC,
			api_input: payload
		});
		let req;
		if (esrsMappingIndex >= 0) {
			req = ReportAIService.getRefinedTextForMaterialTopicESRS(payload);
		} else {
			req = ReportAIService.getRefinedTextForMaterialTopic(payload);
		}		
		req.then(res => {
				setLoading(false);
				if (res.status !== 200) return;
				if (esrsMappingIndex >= 0) {
					setText(res.data.improved_answer);
				} else {
					setText(res.data.example_response);
					delete res.data.example_response;
				}
				
				setRefinedText(res.data);
			})
			.catch(err => { setLoading(false); });
	};

	const getRating = () => {
		// switch (refinedText?.rating) {
		// 	case 1:
		// 		return <span className='text-danger h6'><FiFrown /> {RefinedTextRating({ t }).find(v => v.value === 1).label}</span>;
		// 	case 2: return <span className='text-warning h6'><BsEmojiNeutral /> {RefinedTextRating({ t }).find(v => v.value === 2).label}</span>;
		// 	case 3:
		// 	default:
		// 		return <span className='text-success h6'><FiSmile /> {RefinedTextRating({ t }).find(v => v.value === 3).label}</span>
		// }
		let rating = refinedText?.rating;

		if (rating >= 1 && rating <= 3) {
			return (
				<span className="text-danger h6">
					<FiFrown /> {RefinedTextRating({ t }).find(v => v.value === 1).label}
				</span>
			);
		} else if (rating >= 4 && rating <= 6) {
			return (
				<span className="text-warning h6">
					<BsEmojiNeutral /> {RefinedTextRating({ t }).find(v => v.value === 2).label}
				</span>
			);
		} else if (rating >= 7 && rating <= 10) {
			return (
				<span className="text-success h6">
					<FiSmile /> {RefinedTextRating({ t }).find(v => v.value === 3).label}
				</span>
			);
		} else {
			// Handle any edge cases or undefined ratings
			return null;
		}

	};

	const renderRefinedTextResponse = () => {
		if (type === 'BRSR_STUDIO' || (refinedText.rating < 0 && !refinedText.help_text)) return <></>;

		return (
			<div className='px-1 2'>
				{
					refinedText?.help_text &&
					<>
						<h6><b>{t("HelpText.label")}</b></h6>
						<div dangerouslySetInnerHTML={createMarkup(Utils.convertMarkdownToHTML(refinedText.help_text))}></div>
						{
							refinedText.good && refinedText.improvement &&
							<>
								<hr></hr>
								<p className='text-muted'>{t("RefinedTextValidationHelper1.label")}</p>
								<h6><b>{t("WhatIsGood.label")}</b></h6>
								<div className='pb-3' dangerouslySetInnerHTML={createMarkup(Utils.convertMarkdownToHTML(refinedText.good))}></div>
								<h6><b>{t("Improvements.label")}</b></h6>
								<div dangerouslySetInnerHTML={createMarkup(Utils.convertMarkdownToHTML(refinedText.improvement))}></div>
							</>
						}
					</>
				}
				{
					refinedText?.rating >= 0 &&
					<p><b>{t('YourResponseIs.label')}</b> &nbsp;{getRating()}</p>
				}
				{
					refinedText?.positive_reason_for_rating || refinedText?.negative_reason_for_rating
						?
						<>
							<p className='text-muted'>{t("RefinedTextValidationHelper1.label")}</p>
							{
								refinedText?.positive_reason_for_rating &&
								<p className='d-flex'>
									<span className='text-success h5'><BiLike /></span>
									<span>{refinedText?.positive_reason_for_rating}</span>
								</p>
							}
							{
								refinedText?.negative_reason_for_rating &&
								<p className='d-flex'>
									<span className='text-danger h5'><BiDislike /></span>
									<span>{refinedText?.negative_reason_for_rating}</span>
								</p>
							}
						</>
						:
						<></>
				}
				{
					type != 'CONTENT_BLOCK' &&
					<hr></hr>
				}
			</div>
		)
	};

	const fetchESGHighlightsDataWithMetrics = () => {
		if (location?.state?.esgData) {
			const payload = {
				job_id: jobId,
				company: {
					company_id: companyId,
					company_info: companyInfo?.about ?? '',
					company_name: companyName,
					country: companyInfo?.address?.country?.name ?? '',
					esg_frameworks: companyInfo?.esgFrameworks ?? '',
					esg_goals: companyInfo?.esgGoals ?? '',
					industry: companyInfo?.industry?.name ?? '',
					reporting_standard: regulationName ?? '',
					reporting_year: periodYear
				},
				sections: location.state.esgData.sections,
				is_return: true,
				language: preference.language === 'en' ? 'en-us' : preference.language 
			};
			setLoading(true);
			setLastAPICallContext({
				api_name: API_ROUTES.REPORT_AI.ESG_HIGHLIGHTS,
				api_input: payload
			});
			ReportAIService.generateTextForESGHighlights(payload)
				.then(res => {
					setLoading(false);
					if (res.status !== 200) return;

					setText(res.data);
				})
				.catch(err => { setLoading(false); });
		} else {
			setLoading(true);
			ReportService.getESGHighlightsData(regulationId, `${periodYear}-01-01`)
				.then(res => {
					setLoading(false);
					if (res.status !== 200) return;

					const payload = {
						job_id: jobId,
						company: {
							company_id: companyId,
							company_info: companyInfo?.about ?? '',
							company_name: companyName,
							country: companyInfo?.address?.country?.name ?? '',
							esg_frameworks: companyInfo?.esgFrameworks ?? '',
							esg_goals: companyInfo?.esgGoals ?? '',
							industry: companyInfo?.industry?.name ?? '',
							reporting_standard: regulationName ?? '',
							reporting_year: periodYear
						},
						sections: res.data.sections,
						is_return: true,
						language: preference.language === 'en' ? 'en-us' : preference.language 
					};
					setLoading(true);
					setLastAPICallContext({
						api_name: API_ROUTES.REPORT_AI.ESG_HIGHLIGHTS,
						api_input: payload
					});
					ReportAIService.generateTextForESGHighlights(payload)
						.then(res => {
							setLoading(false);
							if (res.status !== 200) return;

							setText(res.data);
						})
						.catch(err => { setLoading(false); });
				})
				.catch(err => {
					setLoading(false);
				});
		}
	};

	const renderESGHighlightsResponse = () => {
		return (
			<div className='ESGHighlightsResponse_box01'>
				{
					loading
						? renderSpinnerLoader()
						:
						<div>
							<ul>
								{
									text.AnalysisResult?.map(d => {
										return (
											<li>
												<h6 className='fw-bold'>{d.title}</h6>
												<h6 className='fw-bold'>{d.Highlight_Value}</h6>
												<p>{d.Description}</p>
											</li>
										)
									})
								}
							</ul>
							<div className='pt-3 px-2 pb-2'>
								<div className='responsive_qusCard justify-content-end'>
									<ul className='ESGAI_Clipboard-EditeBox'>
										<li>
											<button className='btn btn-secondary'
												onClick={() => onTransferToEditor(null, text)}>
												{t('TransferToEditor.label')}
											</button>
										</li>
									</ul>
								</div>
							</div>
						</div>
				}
			</div>
		);
	};

	const errorHandler = (error) => {
		const _ = ReportingUtils.decodeErrorMessage(error?.status, '',t);
		toast.error(
			<div>
				<h6>{_.header}</h6>
				<div>{_.message}</div>
			</div>, {
			position: toast.POSITION.TOP_RIGHT,
			autoClose: false
		});
		return Promise.reject(error);
	};

	const renderAIConfig = () => {
		if (type === 'MATERIAL_TOPIC'
			|| type === 'BRSR_STUDIO' || type === 'CONTENT_BLOCK'
			|| page?.type === ReportPageType.ESG_HIGHLIGHTS) return <></>;

		return (
			<div className='ESGCopilot_Config_section'>
				<div className='card mb-1'>
					<div className='card-body p-1'>

						<div className='Personality_listCard'>
							<div className="Personality_listCard-head">
								<h6>{t('Style.label')}</h6>
							</div>
							<div className="Personality_listCard-body">
								{
									styles?.map((p, pIndex) =>
										<AIConfigButton
											id={`checkbutton-${p.style_id}-${pIndex}`}
											key={`checkbutton-${p.style_id}-${pIndex}`}
											name={p.styleName}
											value={p.type}
											checked={activeStyles?.indexOf(p.type) >= 0}
											classes={
												activeStyles?.indexOf(p.type) >= 0
													? 'outlined-checkbox-btn-success-checked'
													: 'outlined-checkbox-btn-success'
											}
											onCheck={(e) => {
												setActiveStyles([p.type]);
												// Reset selected tones
												setActiveTones([]);
											}}
										/>
									)
								}
							</div>
						</div>

					</div>
				</div>
				<div className='card mb-1'>
					<div className='card-body p-1'>

						<div className='Personality_listCard'>
							<div className="Personality_listCard-head">
								<h6>{t('Tone.label')}</h6>
							</div>
							<div className="Personality_listCard-body">
								{tones?.map((p, pIndex) => renderToneButton(p, pIndex))}
							</div>
						</div>

					</div>
				</div>
				<div className='card'>
					<div className='card-body p-1'>

						<div className='word_lengthCArd'>
							<div className='word_lengthCArd_head'>
								<h6>{t('Length.label')}</h6>
							</div>
							<div className='word_lengthCArd_body'>

								<AIConfigButton
									id='configButtonShort'
									key='configButtonShort'
									name={t('Short.label')}
									value={'150-200'}
									checked={wordCount === '150-200'}
									classes={
										wordCount === '150-200'
											? 'outlined-checkbox-btn-success-checked'
											: 'outlined-checkbox-btn-success'
									}
									toolTip={`150 to 200 words`}
									onCheck={(e) => setWordCount('150-200')}
								/>
								<AIConfigButton
									id='configButtonMedium'
									key='configButtonMedium'
									name={t('Medium.label')}
									value={'200-350'}
									checked={wordCount === '200-350'}
									classes={
										wordCount === '200-350'
											? 'outlined-checkbox-btn-success-checked'
											: 'outlined-checkbox-btn-success'
									}
									toolTip={`200 to 350 words`}
									onCheck={(e) => setWordCount('200-350')}
								/>
								<AIConfigButton
									id='configButtonLong'
									key='configButtonLong'
									name={t('Long.label')}
									value={'350-500'}
									checked={wordCount === '350-500'}
									classes={
										wordCount === '350-500'
											? 'outlined-checkbox-btn-success-checked'
											: 'outlined-checkbox-btn-success'
									}
									toolTip={`300 to 500 words`}
									onCheck={(e) => setWordCount('350-500')}
								/>
							</div>
						</div>
					</div>
				</div>
				{
					page?.type === ReportPageType.OTHERS &&
					<div className='p-1'>
						<div className='PreviewView_01'>
							<h6>{t('WriteAboutSection.label')}</h6>
						</div>
						<textarea className='form-control' cols='10' rows='2'
							value={description}
							onChange={(e) => setDescription(e.target.value)}>
						</textarea>
					</div>
				}
				<div className='text-center p-2'>
					<button className='btn btn-ESGAi'
						disabled={isGenerateButtonDisabled()}
						onClick={() => generateConfigDependentText()}>
						{t('GenerateDraft.label')}
					</button>
				</div>
				{loading && type === 'REPORTING_STUDIO' && renderSpinnerLoader()}
			</div>
		);
	};

	const isGenerateButtonDisabled = () => {
		if (loading) return true;

		return activeTones?.length === 0 || activeStyles?.length === 0 || (page?.type === ReportPageType.OTHERS && !description);
	}

	const renderToneButton = (p, pIndex) => {
		let isToneDisabled = true;
		for (let index = 0; index < activeStyles.length; index++) {
			const s = activeStyles[index];
			const styleData = styles.find(st => st.type === s);
			if (!styleData) continue;

			if (styleData.compatibleTones?.indexOf(p.type) >= 0) {
				isToneDisabled = false;
				break;
			}
		}
		return (
			<AIConfigButton
				id={`checkbutton-${p.type}-${pIndex}`}
				key={`checkbutton-${p.type}-${pIndex}`}
				name={p.toneName}
				value={p.type}
				checked={activeTones?.indexOf(p.type) >= 0}
				classes={
					activeTones?.indexOf(p.type) >= 0
						? 'outlined-checkbox-btn-success-checked'
						: 'outlined-checkbox-btn-success'
				}
				disabled={isToneDisabled}
				onCheck={(e) => {
					setActiveTones([p.type]);
				}}
			/>
		);
	}

	const feedback = (val) => {
		if (!lastAPICallContext) return;

		ReportAIService.feedback({
			context: lastAPICallContext,
			content: text,
			feedback: val
		}).then(res => {
			if (res.status !== 200) return;

			setFeedbackVal(val);
			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}).catch(err => { });
	};

	const renderSpinnerLoader = () => {
		return (
			<div className='text-center'>
				<Spinner animation="grow" variant="warning" />
				<Spinner animation="grow" variant="warning" />
				<Spinner animation="grow" variant="warning" />
			</div>
		);
	};

	const getRefinedTextForBRSR = () => {
		const payload = {
			question: brsrData.question?.trim() ?? '',
			qualitative_answer: brsrData.answer?.trim() ?? ''
		};
		setLoading(true);
		ReportAIService.getBRSRRephrasedAnswer(payload)
			.then(res => {
				setLoading(false);
				if (res.status !== 200) return;

				setText(res.data.rephrased_answer);
				delete res.data.rephrased_answer;
				setRefinedText(res.data);
			})
			.catch(err => { setLoading(false); });
	};

	const getRefinedTextForContentBlock = () => {
		const payload = {
			question: '',
			qualitative_answer: descriptionData ?? ''
		};
		setLoading(true);
		ReportAIService.getBRSRRephrasedAnswer(payload)
			.then(res => {
				setLoading(false);
				if (res.status !== 200) return;

				setText(res.data.rephrased_answer);
				delete res.data.rephrased_answer;
				setRefinedText(res.data);
			})
			.catch(err => { setLoading(false); });
	};

	const refactorQuantMetrics = (quantitativeMetrics) => {
		quantitativeMetrics.forEach(metric => {
			metric.datapoints = metric.datapoints?.filter(dp => dp.values?.length > 0);
		});
		return quantitativeMetrics.filter(metric => metric.values?.length > 0 || metric.datapoints?.length > 0);
	};

	const refactorQualMetrics = (qualMetrics) => {
		return qualMetrics.filter(metric => metric.questions?.length > 0);
	};

	const createMarkup = (content) => {
		return { __html: content };
	};

	const populateCaseStudies = (cbRes, caseStudies) => {
		cbRes?.forEach(cb => {
			caseStudies.push({
				Title: cb.name,
				Topics: cb.topic?.name ?? '',
				About: cb.about,
				Description: cb.description,
			});
		});
	};

	return (
		<div id='textGenWidget' className='1'>
			<div className='widget-header text-white'>
				<span className='widget-header_inner'><img src={esg_ai} width={15} height={15} /> {t('ESGAICopilot.label')}</span>

				<div className="Assistant_tips-toggle d-none">
					<input type="radio" id="toggle-Assistant" name="Assistant_tips" value="Assistant" checked />
					<label className="radio-button" for="toggle-Assistant">Assistant</label>

					<input type="radio" id="toggle-Tips" name="Assistant_tips" value="Tips" />
					<label className="radio-button" for="toggle-Tips"> Tips</label>
				</div>
				<span className="cursor" onClick={() => onHide()}><AiOutlineClose /></span>
			</div>
			<div className='widget-body px-2 pt-2'>
				<div className='AIwidget-body_in 2'>
					<div className=''>
						{!loading && renderRefinedTextResponse()}
						{loading && (type === 'MATERIAL_TOPIC' || type === 'BRSR_STUDIO' || type === 'CONTENT_BLOCK') && renderSpinnerLoader()}
						{!loading && refinedText?.rating >= 0 && <p className='text-muted'>{t("RefinedTextValidationHelper2.label")}</p>}
						{/* {((page?.type===ReportPageType.ENERGY) || !loading) && renderAIConfig()} */}
						{renderAIConfig()}
						{
							// && !refinedText?.help_text
							!loading && page?.type !== ReportPageType.ESG_HIGHLIGHTS  &&
							<div className=' Generate_Draft_previewbox'>
								<div className='p-1'>
									<div className='PreviewView_01'>
										<BiSolidMagicWand />
										<h6>{t('Preview.label')}</h6>
									</div>
									<div className='PreviewView_02'>
										<textarea className='form-control' cols='10' rows='15'
											value={text}
											ref={textArea}
											onChange={() => null}>
										</textarea>
									</div>
								</div>
								<div className='px-1'>
									<div className='responsive_qusCard'>
										{
											(type !== 'BRSR_STUDIO' && type !== 'CONTENT_BLOCK') &&
											<ul>
												<li>
													{
														feedbackVal === 1
															? <BiSolidLike />
															: <BiLike onClick={() => feedback(1)} />
													}
												</li>
												<li>
													{
														feedbackVal === 0
															? <BiSolidDislike />
															: <BiDislike onClick={() => feedback(0)} />
													}
												</li>
												{/* <li>
												<BsArrowClockwise />
											</li> */}
											</ul>
										}
										{
											!loading && page?.type !== ReportPageType.ESG_HIGHLIGHTS &&
											<ul className='ESGAI_Clipboard-EditeBox'>
												<li>
													<button className='btn btn-secondary'
														onClick={() => copyToClipboard()}>
														{t('CopyContent.label')}
													</button>
												</li>

												{
													type === 'REPORTING_STUDIO' &&
													<li>
														<button className='btn btn-secondary'
															onClick={() => onTransferToEditor(text, null)}>
															{t('TransferToEditor.label')}
														</button>
													</li>
												}
											</ul>
										}
									</div>
								</div>
							</div>
						}
						{
							page?.type === ReportPageType.ESG_HIGHLIGHTS &&
							renderESGHighlightsResponse()
						}

					</div>
					<div className='Tips_box d-none'>
						<div className='Tips_box_short'>
							This is a community driven project to help consolidate the knowledge of those `building`, `operating`, `supporting` and `growing` companies, products and services.
						</div>
						<div className='Tips_box_Additional'>
							<h6>Additional Resources</h6>
							<div className='Tips_box_list'>
								<ul>
									<li>
										<div className='Tips_box_list_img'>
											<img src={test} />
										</div>
										<div>
											<h6>DevRev OneCRM: The Power of One</h6>
											<p>Customer relationship is everything. It doesn't start and end with the sales, marketing, or support teams.</p>
										</div>
									</li>
									<li>
										<div className='Tips_box_list_img'>
											<img src={test} />
										</div>
										<div>
											<h6>The latest and greatest from the DevRev team</h6>
											<p>Check out this month’s changelog, which includes product enhancements, integrations, and loads more.</p>
										</div>
									</li>
								</ul>

							</div>
						</div>
					</div>
				</div>
				{
					!loading && page?.type !== ReportPageType.ESG_HIGHLIGHTS &&
					<div className='text-center AITextGenWidge_footerBox'>
						{
							uiElementsConfig?.texts?.copilotHelp
								?
								uiElementsConfig?.texts?.copilotHelp
								:
								<>
									<div className='text-muted Info_textGen'>
										<span>{t('AITextGenWidgetFootNote1.label')}</span>
										<div className="Privacy_termsbox">
											<ul>
												<li>
													<a className="" target="_blank" href={uiElementsConfig?.texts?.termsAndConditions ?? "https://ecodrisil.com/terms-of-use"}>
														Terms & Conditions
													</a>
												</li>
												<li>Feedback @ <a href="mailto:copilot@ecodrisil.com">copilot@ecodrisil.com</a>.

												</li>

											</ul>


										</div>
									</div>
								</>
						}
					</div>
				}
			</div>

		</div>
	);
}

export default withTranslation()(AITextGenWidget);
